import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HelpDrawer } from '@/components/ui/help-drawer';
import modifierGroupsHelp from '@/../docs/help/modifier-groups.md?raw';
import { toast } from '@/hooks/use-toast';
import {
  createModifier,
  createModifierGroup,
  deleteModifier,
  deleteModifierGroup,
  fetchModifierGroups,
  type MenuItemModifier,
  type MenuModifierGroup,
  updateModifier,
  updateModifierGroup,
  reorderModifierGroups,
  reorderModifiers,
  type ReorderModifierGroupInput,
  type ReorderModifierInput,
} from '@/lib/api/menu';
import {
  AlertCircle,
  Ban,
  ChefHat,
  GripVertical,
  Layers,
  Pencil,
  Plus,
  Repeat,
  Sparkles,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type ModifierGroupsEditorProps = {
  itemId: string;
  tenantSlug?: string;
  isOpen: boolean;
  variant?: 'compact' | 'manager';
  title?: string;
  subtitle?: string;
  addButtonLabel?: string;
  headerAddon?: ReactNode;
};

type GroupDialogState =
  | { mode: 'create'; initial?: Partial<MenuModifierGroup> }
  | { mode: 'edit'; initial: MenuModifierGroup }
  | null;

type ModifierDialogState =
  | { mode: 'create'; group: MenuModifierGroup }
  | { mode: 'edit'; group: MenuModifierGroup; modifier: MenuItemModifier }
  | null;

const groupQueryKey = (itemId: string) => ['menu', 'modifierGroups', itemId] as const;

type SortableHandleProps = Pick<ReturnType<typeof useSortable>, 'attributes' | 'listeners'>;

const selectionModes: Array<{ value: MenuModifierGroup['selectionMode']; label: string }> = [
  { value: 'single', label: 'Single selection' },
  { value: 'multiple', label: 'Multiple selections' },
];

type StackingRuleDefinition = {
  value: MenuModifierGroup['stackingBehavior'];
  label: string;
  badge: string;
  description: string;
  helper: string;
  icon: LucideIcon;
};

const stackingRules: StackingRuleDefinition[] = [
  {
    value: 'exclusive',
    label: 'Exclusive choice',
    badge: 'Exclusive',
    description: 'Guests pick a single option; choosing another replaces the previous selection.',
    helper: 'Use for sizes, crusts, or cook temperatures.',
    icon: Ban,
  },
  {
    value: 'stack',
    label: 'Stack selections',
    badge: 'Stack',
    description: 'Guests can stack different modifiers in the same group, perfect for add-ons.',
    helper: 'Ideal for toppings or mix-ins where multiple choices are allowed.',
    icon: Layers,
  },
  {
    value: 'repeatable',
    label: 'Repeatable with quantity',
    badge: 'Repeatable',
    description: 'Guests can add the same modifier multiple times with quantity controls.',
    helper: 'Best when modifiers support multiples, like extra sauces or sides.',
    icon: Repeat,
  },
];

const stackingRuleMap: Record<MenuModifierGroup['stackingBehavior'], StackingRuleDefinition> = stackingRules.reduce(
  (acc, rule) => {
    acc[rule.value] = rule;
    return acc;
  },
  {} as Record<MenuModifierGroup['stackingBehavior'], StackingRuleDefinition>,
);

type AllergenPreset = {
  id: string;
  label: string;
  description: string;
  tags: string[];
  mode: 'merge' | 'replace' | 'clear';
};

const allergenPresets: AllergenPreset[] = [
  {
    id: 'top-9',
    label: 'Contains top 9 allergens',
    description: 'Marks dairy, eggs, fish, shellfish, tree nuts, peanuts, wheat, soy, and sesame.',
    tags: ['Dairy', 'Eggs', 'Fish', 'Shellfish', 'Tree Nuts', 'Peanuts', 'Wheat', 'Soy', 'Sesame'],
    mode: 'merge',
  },
  {
    id: 'contains-nuts',
    label: 'Contains nuts',
    description: 'Quickly flags tree nut and peanut exposure.',
    tags: ['Tree Nuts', 'Peanuts'],
    mode: 'merge',
  },
  {
    id: 'contains-gluten',
    label: 'Contains gluten',
    description: 'Adds gluten and wheat allergen tags.',
    tags: ['Gluten', 'Wheat'],
    mode: 'merge',
  },
  {
    id: 'clear-allergens',
    label: 'Clear allergen tags',
    description: 'Removes existing allergen tags for the selected modifiers.',
    tags: [],
    mode: 'clear',
  },
];

const normalizeAllergenTag = (tag: string): string => tag.trim().toLowerCase();

const sanitizePresetTags = (tags: string[]): string[] =>
  tags
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);

const applyPresetToTags = (existing: string[] | undefined, preset: AllergenPreset): string[] => {
  const safeExisting = (existing ?? []).map((tag) => tag.trim()).filter((tag) => tag.length > 0);

  if (preset.mode === 'clear') {
    return [];
  }

  if (preset.mode === 'replace') {
    return sanitizePresetTags(preset.tags);
  }

  const byTag = new Map<string, string>();
  for (const tag of safeExisting) {
    const normalized = normalizeAllergenTag(tag);
    if (!byTag.has(normalized)) {
      byTag.set(normalized, tag);
    }
  }

  for (const tag of preset.tags) {
    const normalized = normalizeAllergenTag(tag);
    if (!byTag.has(normalized)) {
      byTag.set(normalized, tag.trim());
    }
  }

  return Array.from(byTag.values());
};

const tagsEqual = (left: string[] | undefined, right: string[]): boolean => {
  const safeLeft = (left ?? []).map(normalizeAllergenTag).sort();
  const safeRight = right.map(normalizeAllergenTag).sort();
  if (safeLeft.length !== safeRight.length) {
    return false;
  }
  return safeLeft.every((value, index) => value === safeRight[index]);
};

type StackingRuleSelectorProps = {
  value: MenuModifierGroup['stackingBehavior'];
  onChange: (value: MenuModifierGroup['stackingBehavior']) => void;
  disabled?: boolean;
};

function StackingRuleSelector({ value, onChange, disabled }: StackingRuleSelectorProps): JSX.Element {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-semibold text-foreground">Variant stacking rules</Label>
        <p className="text-xs text-muted-foreground">
          Choose how guests can combine variants or add-ons inside this group.
        </p>
      </div>
      <div
        role="radiogroup"
        aria-label="Variant stacking rules"
        className="grid gap-2"
      >
        {stackingRules.map((rule) => {
          const isActive = rule.value === value;
          return (
            <button
              key={rule.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  onChange(rule.value);
                }
              }}
              className={cn(
                'flex w-full items-start gap-3 rounded-lg border bg-card p-3 text-left transition-colors',
                isActive
                  ? 'border-emerald-500/60 bg-emerald-500/10 shadow-sm'
                  : 'border-border hover:border-foreground/40 hover:bg-accent/20',
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              )}
            >
              <rule.icon
                className={cn(
                  'mt-1 h-4 w-4 shrink-0',
                  isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
                )}
              />
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-foreground">{rule.label}</p>
                  {isActive ? <Badge variant="secondary">Selected</Badge> : null}
                </div>
                <p className="text-xs text-muted-foreground">{rule.description}</p>
                <p className="text-xs text-muted-foreground/80">{rule.helper}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ModifierGroupsEditor({
  itemId,
  tenantSlug,
  isOpen,
  variant = 'compact',
  title = 'Modifier Groups',
  subtitle = 'Configure add-ons, selection rules, and allergen metadata per item.',
  addButtonLabel = 'Add Group',
  headerAddon,
}: ModifierGroupsEditorProps): JSX.Element {
  const queryClient = useQueryClient();
  const [groupDialog, setGroupDialog] = useState<GroupDialogState>(null);
  const [modifierDialog, setModifierDialog] = useState<ModifierDialogState>(null);

  const query = useQuery({
    queryKey: groupQueryKey(itemId),
    queryFn: () => fetchModifierGroups({ itemId, tenantSlug }),
    enabled: isOpen && Boolean(itemId),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isOpen) {
      void query.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const invalidateGroups = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: groupQueryKey(itemId) }).catch(() => undefined);
  }, [itemId, queryClient]);

  const onMutationSuccess = useCallback((message: string) => {
    invalidateGroups();
    toast({ title: message });
  }, [invalidateGroups]);

  const onMutationError = useCallback((error: unknown, context: string) => {
    const description = error instanceof Error ? error.message : 'Please try again.';
    toast({
      title: `${context} failed`,
      description,
      variant: 'destructive',
    });
  }, []);

  const createGroupMutation = useMutation({
    mutationFn: (payload: Parameters<typeof createModifierGroup>[0]) => createModifierGroup(payload),
    onSuccess: () => onMutationSuccess('Modifier group created'),
    onError: (error) => onMutationError(error, 'Create modifier group'),
  });

  const updateGroupMutation = useMutation({
    mutationFn: (payload: Parameters<typeof updateModifierGroup>[0]) => updateModifierGroup(payload),
    onSuccess: () => onMutationSuccess('Modifier group updated'),
    onError: (error) => onMutationError(error, 'Update modifier group'),
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (groupId: string) => deleteModifierGroup(groupId, tenantSlug),
    onSuccess: () => onMutationSuccess('Modifier group removed'),
    onError: (error) => onMutationError(error, 'Delete modifier group'),
  });

  const createModifierMutation = useMutation({
    mutationFn: (payload: Parameters<typeof createModifier>[0]) => createModifier(payload),
    onSuccess: () => onMutationSuccess('Modifier created'),
    onError: (error) => onMutationError(error, 'Create modifier'),
  });

  const updateModifierMutation = useMutation({
    mutationFn: (payload: Parameters<typeof updateModifier>[0]) => updateModifier(payload),
    onSuccess: () => onMutationSuccess('Modifier updated'),
    onError: (error) => onMutationError(error, 'Update modifier'),
  });

  const deleteModifierMutation = useMutation({
    mutationFn: (modifierId: string) => deleteModifier(modifierId, tenantSlug),
    onSuccess: () => onMutationSuccess('Modifier removed'),
    onError: (error) => onMutationError(error, 'Delete modifier'),
  });

  const reorderGroupsMutation = useMutation({
    mutationFn: (payload: { updates: ReorderModifierGroupInput[] }) =>
      reorderModifierGroups(itemId, payload.updates, tenantSlug),
    onError: (error) => onMutationError(error, 'Reorder modifier groups'),
    onSuccess: () => invalidateGroups(),
  });

  const reorderModifiersMutation = useMutation({
    mutationFn: (payload: { groupId: string; updates: ReorderModifierInput[] }) =>
      reorderModifiers(payload.groupId, payload.updates, tenantSlug),
    onError: (error) => onMutationError(error, 'Reorder modifiers'),
    onSuccess: () => invalidateGroups(),
  });

  const isManagerView = variant === 'manager';

  const groups = useMemo(() => {
    if (!query.data) return [];
    return [...query.data].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [query.data]);

  const [orderedGroups, setOrderedGroups] = useState<MenuModifierGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [isBulkActionPending, setIsBulkActionPending] = useState(false);
  const [isAllergenPresetDialogOpen, setIsAllergenPresetDialogOpen] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  const selectedGroups = useMemo(
    () => groups.filter((group) => selectedGroupIds.includes(group.id)),
    [groups, selectedGroupIds],
  );

  const selectedModifierCount = useMemo(
    () => selectedGroups.reduce((total, group) => total + group.modifiers.length, 0),
    [selectedGroups],
  );
  const hasSelectedModifiers = selectedModifierCount > 0;

  useEffect(() => {
    if (isManagerView) {
      setOrderedGroups(groups.map(cloneGroupWithModifiers));
    } else {
      setOrderedGroups([]);
      setSelectedGroupIds([]);
    }
  }, [groups, isManagerView]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedGroupIds([]);
      setIsAllergenPresetDialogOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedGroupIds.length === 0 && isAllergenPresetDialogOpen) {
      setIsAllergenPresetDialogOpen(false);
    }
  }, [isAllergenPresetDialogOpen, selectedGroupIds]);

  const displayGroups = isManagerView
    ? orderedGroups.length > 0 || groups.length === 0
      ? orderedGroups
      : groups
    : groups;

  const handleDeleteGroup = useCallback(
    (group: MenuModifierGroup) => {
      const confirmed = window.confirm(
        `Remove "${group.name}" and detach ${group.modifiers.length} modifier(s)?`,
      );
      if (!confirmed) return;
      deleteGroupMutation.mutate(group.id, {
        onSuccess: () => {
          setSelectedGroupIds((prev) => prev.filter((id) => id !== group.id));
        },
      });
    },
    [deleteGroupMutation],
  );

  const handleDeleteModifier = useCallback(
    (modifier: MenuItemModifier) => {
      const confirmed = window.confirm(`Remove modifier "${modifier.name}"?`);
      if (!confirmed) return;
      deleteModifierMutation.mutate(modifier.id);
    },
    [deleteModifierMutation],
  );

  const isBusy =
    createGroupMutation.isPending ||
    updateGroupMutation.isPending ||
    deleteGroupMutation.isPending ||
    createModifierMutation.isPending ||
    updateModifierMutation.isPending ||
    deleteModifierMutation.isPending ||
    reorderGroupsMutation.isPending ||
    reorderModifiersMutation.isPending ||
    isBulkActionPending;

  const toggleGroupSelection = useCallback((groupId: string, next: boolean) => {
    setSelectedGroupIds((prev) => {
      if (next) {
        return prev.includes(groupId) ? prev : [...prev, groupId];
      }
      return prev.filter((id) => id !== groupId);
    });
  }, []);

  const handleSelectAllGroups = useCallback(
    (checked: boolean) => {
      if (!isManagerView) return;
      if (checked) {
        setSelectedGroupIds(displayGroups.map((group) => group.id));
      } else {
        setSelectedGroupIds([]);
      }
    },
    [displayGroups, isManagerView],
  );

  const handleBulkSetRequired = useCallback(
    async (value: boolean) => {
      if (selectedGroupIds.length === 0) return;
      setIsBulkActionPending(true);
      try {
        await Promise.all(
          selectedGroupIds.map((groupId) =>
            updateModifierGroup({ groupId, tenantSlug, isRequired: value }),
          ),
        );
        onMutationSuccess(
          value ? 'Marked selected modifier groups as required' : 'Marked selected modifier groups as optional',
        );
        setSelectedGroupIds([]);
        invalidateGroups();
      } catch (error) {
        onMutationError(error, 'Bulk update modifier groups');
      } finally {
        setIsBulkActionPending(false);
      }
    },
    [invalidateGroups, onMutationError, onMutationSuccess, selectedGroupIds, tenantSlug],
  );

  const handleBulkSetStacking = useCallback(
    async (stacking: MenuModifierGroup['stackingBehavior']) => {
      if (selectedGroupIds.length === 0) return;
      setIsBulkActionPending(true);
      try {
        await Promise.all(
          selectedGroupIds.map((groupId) =>
            updateModifierGroup({ groupId, tenantSlug, stackingBehavior: stacking }),
          ),
        );
        onMutationSuccess('Updated stacking behavior for selected modifier groups');
        setSelectedGroupIds([]);
        invalidateGroups();
      } catch (error) {
        onMutationError(error, 'Bulk update stacking behavior');
      } finally {
        setIsBulkActionPending(false);
      }
    },
    [invalidateGroups, onMutationError, onMutationSuccess, selectedGroupIds, tenantSlug],
  );

  const handleBulkApplyAllergenPreset = useCallback(
    async (presetId: string) => {
      const preset = allergenPresets.find((option) => option.id === presetId);
      if (!preset) return;
      if (selectedGroups.length === 0) {
        setIsAllergenPresetDialogOpen(false);
        return;
      }

      const updates = selectedGroups.reduce<
        Array<{ modifierId: string; allergenTags: string[] }>
      >((accumulator, group) => {
        for (const modifier of group.modifiers) {
          const nextTags = applyPresetToTags(modifier.allergenTags, preset);
          if (!tagsEqual(modifier.allergenTags, nextTags)) {
            accumulator.push({ modifierId: modifier.id, allergenTags: nextTags });
          }
        }
        return accumulator;
      }, []);

      if (updates.length === 0) {
        toast({
          title: 'No allergen changes needed',
          description: 'Selected modifiers already match that preset.',
        });
        setIsAllergenPresetDialogOpen(false);
        return;
      }

      setIsBulkActionPending(true);
      try {
        await Promise.all(
          updates.map((update) =>
            updateModifier({
              modifierId: update.modifierId,
              allergenTags: update.allergenTags,
              tenantSlug,
            }),
          ),
        );
        onMutationSuccess(
          `Applied "${preset.label}" to ${updates.length} modifier${updates.length === 1 ? '' : 's'}`,
        );
        setSelectedGroupIds([]);
        setIsAllergenPresetDialogOpen(false);
      } catch (error) {
        onMutationError(error, 'Apply allergen preset');
      } finally {
        setIsBulkActionPending(false);
      }
    },
    [onMutationError, onMutationSuccess, selectedGroups, tenantSlug],
  );

  const handleBulkDeleteGroups = useCallback(async () => {
    if (selectedGroupIds.length === 0) return;
    const confirmed = window.confirm(
      `Delete ${selectedGroupIds.length} modifier group${selectedGroupIds.length > 1 ? 's' : ''}? This cannot be undone.`,
    );
    if (!confirmed) return;
    setIsBulkActionPending(true);
    try {
      await Promise.all(selectedGroupIds.map((groupId) => deleteModifierGroup(groupId, tenantSlug)));
      onMutationSuccess('Removed selected modifier groups');
      setSelectedGroupIds([]);
      invalidateGroups();
    } catch (error) {
      onMutationError(error, 'Bulk delete modifier groups');
    } finally {
      setIsBulkActionPending(false);
    }
  }, [invalidateGroups, onMutationError, onMutationSuccess, selectedGroupIds, tenantSlug]);

  const handleGroupDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!isManagerView) return;
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      setOrderedGroups((current) => {
        const oldIndex = current.findIndex((group) => group.id === active.id);
        const newIndex = current.findIndex((group) => group.id === over.id);
        if (oldIndex === -1 || newIndex === -1) {
          return current;
        }
        const reordered = arrayMove(current, oldIndex, newIndex).map((group, index) => ({
          ...group,
          sortOrder: index,
          modifiers: group.modifiers.map((modifier) => ({ ...modifier })),
        }));
        const updates: ReorderModifierGroupInput[] = reordered.map((group, index) => ({
          id: group.id,
          sortOrder: index,
        }));
        reorderGroupsMutation.mutate({ updates });
        return reordered;
      });
    },
    [isManagerView, reorderGroupsMutation],
  );

  const handleModifierDragEnd = useCallback(
    (groupId: string) =>
      (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
          return;
        }
        setOrderedGroups((current) => {
          const groupIndex = current.findIndex((group) => group.id === groupId);
          if (groupIndex === -1) {
            return current;
          }
          const group = current[groupIndex];
          const oldIndex = group.modifiers.findIndex((modifier) => modifier.id === active.id);
          const newIndex = group.modifiers.findIndex((modifier) => modifier.id === over.id);
          if (oldIndex === -1 || newIndex === -1) {
            return current;
          }
          const reorderedModifiers = arrayMove(group.modifiers, oldIndex, newIndex).map(
            (modifier, index) => ({
              ...modifier,
              sortOrder: index,
            }),
          );
          const updates: ReorderModifierInput[] = reorderedModifiers.map((modifier, index) => ({
            id: modifier.id,
            sortOrder: index,
          }));
          reorderModifiersMutation.mutate({ groupId, updates });
          const next = [...current];
          next[groupIndex] = {
            ...group,
            modifiers: reorderedModifiers,
          };
          return next;
        });
      },
    [reorderModifiersMutation],
  );

  const selectedCount = selectedGroupIds.length;
  const totalGroups = displayGroups.length;
  const selectAllValue: boolean | 'indeterminate' =
    selectedCount === 0 ? false : selectedCount === totalGroups ? true : 'indeterminate';

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <HelpDrawer
            title="Modifier groups guide"
            description="Reference stacking rules, required selections, and allergen updates."
            markdown={modifierGroupsHelp}
            analyticsId="modifier-groups-help"
            triggerLabel="How it works"
          />
          <Button
            size="sm"
            onClick={() =>
              setGroupDialog({
                mode: 'create',
                initial: {
                  itemId,
                  selectionMode: 'single',
                  stackingBehavior: 'exclusive',
                  minSelections: 0,
                  maxSelections: null,
                  allowQuantity: false,
                  allowCustomPrice: false,
                  isRequired: false,
                } as Partial<MenuModifierGroup>,
              })
            }
            disabled={!itemId}
          >
            <Plus className="mr-2 h-4 w-4" />
            {addButtonLabel}
          </Button>
          {headerAddon}
        </div>
      </div>

      {query.isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      ) : query.isError ? (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 text-destructive" />
            <div className="space-y-2">
              <div>
                <p className="text-sm font-semibold text-destructive">
                  We couldn&apos;t load modifier groups
                </p>
                <p className="text-xs text-destructive/80">
                  Check your network connection and try again. Changes are safe if already saved.
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => query.refetch()}
                disabled={query.isFetching}
              >
                Retry loading
              </Button>
            </div>
          </div>
        </div>
      ) : displayGroups.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-muted-foreground/40 bg-muted/20 px-8 py-10 text-center">
          <ChefHat className="mb-4 h-10 w-10 text-muted-foreground/60" />
          <p className="text-sm text-muted-foreground">
            Start by creating a modifier group to manage add-ons, variants, stacking rules, and allergen presets.
          </p>
          <Button
            size="sm"
            className="mt-4"
            variant="secondary"
            onClick={() =>
              setGroupDialog({
                mode: 'create',
                initial: {
                  itemId,
                  selectionMode: 'single',
                  stackingBehavior: 'exclusive',
                  minSelections: 0,
                  maxSelections: null,
                  allowQuantity: false,
                  allowCustomPrice: false,
                  isRequired: false,
                } as Partial<MenuModifierGroup>,
              })
            }
            disabled={!itemId}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create modifier group
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {isManagerView ? (
            <>
              <div className="flex flex-col gap-3 rounded-md border border-border/60 bg-muted/20 p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="modifier-groups-select-all"
                      checked={selectAllValue}
                      onCheckedChange={(checked) => handleSelectAllGroups(Boolean(checked))}
                      aria-label="Select all modifier groups"
                      disabled={isBusy}
                    />
                    <label htmlFor="modifier-groups-select-all" className="text-sm font-medium">
                      {selectedCount > 0
                        ? `${selectedCount} selected`
                        : 'Select modifier groups'}
                    </label>
                  </div>
                  {selectedCount > 0 ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulkSetRequired(true)}
                        disabled={isBulkActionPending}
                      >
                        Mark required
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulkSetRequired(false)}
                        disabled={isBulkActionPending}
                      >
                        Mark optional
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" disabled={isBulkActionPending}>
                            Stacking
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => handleBulkSetStacking('exclusive')}>
                            Exclusive
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => handleBulkSetStacking('stack')}>
                            Stack
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => handleBulkSetStacking('repeatable')}>
                            Repeatable
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsAllergenPresetDialogOpen(true)}
                        disabled={isBulkActionPending || !hasSelectedModifiers}
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        Allergen presets
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleBulkDeleteGroups}
                        disabled={isBulkActionPending}
                      >
                        Delete
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleGroupDragEnd}
              >
                <SortableContext
                  items={displayGroups.map((group) => group.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {displayGroups.map((group) => (
                    <ManagerGroupCard
                      key={group.id}
                      group={group}
                      disableActions={isBusy}
                      selected={selectedGroupIds.includes(group.id)}
                      onToggleSelect={(next) => toggleGroupSelection(group.id, next)}
                      onEditGroup={() => setGroupDialog({ mode: 'edit', initial: group })}
                      onDeleteGroup={() => handleDeleteGroup(group)}
                      onAddModifier={() => setModifierDialog({ mode: 'create', group })}
                      onEditModifier={(modifier) =>
                        setModifierDialog({ mode: 'edit', group, modifier })
                      }
                      onDeleteModifier={handleDeleteModifier}
                      onModifierDragEnd={handleModifierDragEnd(group.id)}
                      modifierDragDisabled={isBusy}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </>
          ) : (
            displayGroups.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                variant="compact"
                disableActions={isBusy}
                onEditGroup={() => setGroupDialog({ mode: 'edit', initial: group })}
                onDeleteGroup={() => handleDeleteGroup(group)}
                onAddModifier={() => setModifierDialog({ mode: 'create', group })}
                onEditModifier={(modifier) => setModifierDialog({ mode: 'edit', group, modifier })}
                onDeleteModifier={handleDeleteModifier}
              />
            ))
          )}
        </div>
      )}

      <Dialog
        open={isAllergenPresetDialogOpen}
        onOpenChange={(open) => {
          if (!isBulkActionPending) {
            setIsAllergenPresetDialogOpen(open);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply allergen preset</DialogTitle>
            <DialogDescription>
              {hasSelectedModifiers
                ? `Will update ${selectedModifierCount} modifier${selectedModifierCount === 1 ? '' : 's'} across ${selectedGroups.length} group${selectedGroups.length === 1 ? '' : 's'}.`
                : 'Select modifier groups with modifiers to enable presets.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {allergenPresets.map((preset) => {
              const Icon = preset.mode === 'clear' ? Trash2 : Sparkles;
              const disabled = isBulkActionPending || !hasSelectedModifiers;
              return (
                <button
                  key={preset.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleBulkApplyAllergenPreset(preset.id)}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-lg border bg-card p-3 text-left transition-colors',
                    preset.mode === 'clear'
                      ? 'hover:border-destructive/70 hover:bg-destructive/10'
                      : 'hover:border-emerald-500/60 hover:bg-emerald-500/10',
                    disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                  )}
                >
                  <Icon
                    className={cn(
                      'mt-1 h-4 w-4 shrink-0',
                      preset.mode === 'clear'
                        ? 'text-destructive'
                        : 'text-emerald-600 dark:text-emerald-400',
                    )}
                  />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">{preset.label}</p>
                    <p className="text-xs text-muted-foreground">{preset.description}</p>
                    {preset.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {preset.tags.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-[11px]">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground/80">
                        Removes all allergen tags from modifiers.
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter className="sm:justify-between">
            <p className="text-xs text-muted-foreground">
              Presets update modifiers instantly and log changes for audit history.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAllergenPresetDialogOpen(false)}
              disabled={isBulkActionPending}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ModifierGroupDialog
        state={groupDialog}
        onClose={() => setGroupDialog(null)}
        onSubmit={async (values) => {
          if (groupDialog?.mode === 'edit' && groupDialog.initial) {
            await updateGroupMutation.mutateAsync({
              groupId: groupDialog.initial.id,
              tenantSlug,
              ...values,
            });
          } else if (groupDialog?.mode === 'create') {
            await createGroupMutation.mutateAsync({
              itemId,
              tenantSlug,
              ...values,
            });
          }
          setGroupDialog(null);
        }}
        isBusy={createGroupMutation.isPending || updateGroupMutation.isPending}
      />

      <ModifierDialog
        state={modifierDialog}
        onClose={() => setModifierDialog(null)}
        onSubmit={async (values) => {
          if (!modifierDialog) return;
          if (modifierDialog.mode === 'edit') {
            await updateModifierMutation.mutateAsync({
              modifierId: modifierDialog.modifier.id,
              tenantSlug,
              ...values,
            });
          } else {
            await createModifierMutation.mutateAsync({
              groupId: modifierDialog.group.id,
              tenantSlug,
              ...values,
            });
          }
          setModifierDialog(null);
        }}
        isBusy={createModifierMutation.isPending || updateModifierMutation.isPending}
      />

      {isBusy ? <p className="text-xs text-muted-foreground">Saving changes…</p> : null}
    </div>
  );
}

type GroupCardProps = {
  group: MenuModifierGroup;
  variant: 'compact' | 'manager';
  disableActions: boolean;
  selected?: boolean;
  onToggleSelect?: (selected: boolean) => void;
  dragHandleProps?: SortableHandleProps;
  onEditGroup: () => void;
  onDeleteGroup: () => void;
  onAddModifier: () => void;
  onEditModifier: (modifier: MenuItemModifier) => void;
  onDeleteModifier: (modifier: MenuItemModifier) => void;
  modifiersContent?: ReactNode;
};

function GroupCard({
  group,
  variant,
  disableActions,
  selected = false,
  onToggleSelect,
  dragHandleProps,
  onEditGroup,
  onDeleteGroup,
  onAddModifier,
  onEditModifier,
  onDeleteModifier,
  modifiersContent,
}: GroupCardProps): JSX.Element {
  const stackingRule = stackingRuleMap[group.stackingBehavior] ?? stackingRuleMap.exclusive;

  const content = modifiersContent ?? (
    <DefaultModifierList
      modifiers={group.modifiers}
      onEditModifier={onEditModifier}
      onDeleteModifier={onDeleteModifier}
      disableActions={disableActions}
      variant={variant}
    />
  );

  return (
    <Card className={cn(variant === 'manager' && selected && 'border-primary shadow-sm')}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-3">
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex items-start gap-2">
            {variant === 'manager' ? (
              <>
                <Checkbox
                  checked={selected}
                  onCheckedChange={(checked) => onToggleSelect?.(Boolean(checked))}
                  disabled={disableActions}
                  aria-label={`Select modifier group ${group.name}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="cursor-grab text-muted-foreground"
                  disabled={disableActions}
                  {...(dragHandleProps?.attributes ?? {})}
                  {...(dragHandleProps?.listeners ?? {})}
                >
                  <GripVertical className="h-4 w-4" />
                  <span className="sr-only">Reorder group</span>
                </Button>
              </>
            ) : null}
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                {group.name}
                {group.isRequired ? <Badge variant="secondary">Required</Badge> : null}
                {group.kind === 'variant' ? <Badge variant="outline">Variant group</Badge> : null}
              </CardTitle>
              <CardDescription>
                {group.description ? `${group.description} · ` : ''}
                {group.selectionMode === 'multiple' ? 'Multiple selections' : 'Single selection'}
                {group.selectionMode === 'multiple'
                  ? ` · Min ${group.minSelections ?? 0}${
                      group.maxSelections !== null && group.maxSelections !== undefined
                        ? ` / Max ${group.maxSelections}`
                        : ''
                    }`
                  : ''}
                {group.allowQuantity ? ' · Quantity enabled' : ''}
                {group.allowCustomPrice ? ' · Custom price' : ''}
              </CardDescription>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="icon" onClick={onEditGroup} disabled={disableActions}>
            <Pencil className="h-4 w-4" />
            <span className="sr-only">Edit</span>
          </Button>
          <Button variant="ghost" size="icon" onClick={onDeleteGroup} disabled={disableActions}>
            <Trash2 className="h-4 w-4" />
            <span className="sr-only">Delete</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className={cn(
                      'flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium',
                      stackingRule.value === 'exclusive'
                        ? 'border-border'
                        : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
                    )}
                  >
                    <stackingRule.icon className="h-3.5 w-3.5" />
                    {stackingRule.badge}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs space-y-1">
                  <p className="font-medium text-foreground">{stackingRule.label}</p>
                  <p className="text-xs text-muted-foreground">{stackingRule.description}</p>
                  <p className="text-xs text-muted-foreground/80">{stackingRule.helper}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Badge variant="outline" className="rounded-full">
              Sort order: {group.sortOrder}
            </Badge>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-sm font-semibold">Modifiers</h4>
              <Button size="sm" variant="secondary" onClick={onAddModifier} disabled={disableActions}>
                <Plus className="mr-2 h-4 w-4" />
                Add Modifier
              </Button>
            </div>
            {content}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type ManagerGroupCardProps = {
  group: MenuModifierGroup;
  disableActions: boolean;
  selected: boolean;
  onToggleSelect: (selected: boolean) => void;
  onEditGroup: () => void;
  onDeleteGroup: () => void;
  onAddModifier: () => void;
  onEditModifier: (modifier: MenuItemModifier) => void;
  onDeleteModifier: (modifier: MenuItemModifier) => void;
  onModifierDragEnd: (event: DragEndEvent) => void;
  modifierDragDisabled: boolean;
};

function ManagerGroupCard({
  group,
  disableActions,
  selected,
  onToggleSelect,
  onEditGroup,
  onDeleteGroup,
  onAddModifier,
  onEditModifier,
  onDeleteModifier,
  onModifierDragEnd,
  modifierDragDisabled,
}: ManagerGroupCardProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: group.id,
    disabled: disableActions,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
  };

  const modifierSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  const hasModifiers = group.modifiers.length > 0;

  const modifiersContent = hasModifiers ? (
    <DndContext
      sensors={modifierSensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={onModifierDragEnd}
    >
      <SortableContext
        items={group.modifiers.map((modifier) => modifier.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {group.modifiers.map((modifier) => (
            <SortableModifierRow
              key={modifier.id}
              modifier={modifier}
              disabled={modifierDragDisabled}
              onEdit={() => onEditModifier(modifier)}
              onDelete={() => onDeleteModifier(modifier)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  ) : undefined;

  return (
    <div ref={setNodeRef} style={style}>
      <GroupCard
        group={group}
        variant="manager"
        disableActions={disableActions}
        selected={selected}
        onToggleSelect={onToggleSelect}
        dragHandleProps={{ attributes, listeners }}
        onEditGroup={onEditGroup}
        onDeleteGroup={onDeleteGroup}
        onAddModifier={onAddModifier}
        onEditModifier={onEditModifier}
        onDeleteModifier={onDeleteModifier}
        modifiersContent={modifiersContent}
      />
    </div>
  );
}

type DefaultModifierListProps = {
  modifiers: MenuItemModifier[];
  onEditModifier: (modifier: MenuItemModifier) => void;
  onDeleteModifier: (modifier: MenuItemModifier) => void;
  disableActions: boolean;
  variant: 'compact' | 'manager';
};

function DefaultModifierList({
  modifiers,
  onEditModifier,
  onDeleteModifier,
  disableActions,
  variant,
}: DefaultModifierListProps): JSX.Element {
  if (modifiers.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-muted-foreground/40 p-3 text-sm text-muted-foreground">
        No modifiers yet.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {modifiers.map((modifier) => (
        <ModifierRow
          key={modifier.id}
          modifier={modifier}
          variant={variant}
          disableActions={disableActions}
          onEdit={() => onEditModifier(modifier)}
          onDelete={() => onDeleteModifier(modifier)}
        />
      ))}
    </div>
  );
}

type ModifierRowProps = {
  modifier: MenuItemModifier;
  variant: 'compact' | 'manager';
  onEdit: () => void;
  onDelete: () => void;
  disableActions: boolean;
  dragHandleProps?: SortableHandleProps;
};

function ModifierRow({
  modifier,
  variant,
  onEdit,
  onDelete,
  disableActions,
  dragHandleProps,
}: ModifierRowProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-1 items-start gap-2">
        {variant === 'manager' && dragHandleProps ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="cursor-grab text-muted-foreground"
            disabled={disableActions}
            {...(dragHandleProps.attributes ?? {})}
            {...(dragHandleProps.listeners ?? {})}
          >
            <GripVertical className="h-4 w-4" />
            <span className="sr-only">Reorder modifier</span>
          </Button>
        ) : null}
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">{modifier.name}</p>
            {modifier.isDefault ? (
              <Badge variant="secondary" className="rounded-full">
                Default
              </Badge>
            ) : null}
            {modifier.isActive ? null : (
              <Badge variant="outline" className="rounded-full">
                Inactive
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Price delta: {formatPrice(modifier.priceDeltaCents)}
            {modifier.priceOverrideCents !== null
              ? ` · Override: ${formatPrice(modifier.priceOverrideCents)}`
              : ''}
            {modifier.minQuantity || modifier.maxQuantity !== null
              ? ` · Qty ${modifier.minQuantity ?? 0}${
                  modifier.maxQuantity !== null
                    ? `-${modifier.maxQuantity}`
                    : '+'
                }`
              : ''}
          </p>
          {modifier.allergenTags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {modifier.allergenTags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="ghost" size="icon" onClick={onEdit} disabled={disableActions}>
          <Pencil className="h-4 w-4" />
          <span className="sr-only">Edit modifier</span>
        </Button>
        <Button variant="ghost" size="icon" onClick={onDelete} disabled={disableActions}>
          <Trash2 className="h-4 w-4" />
          <span className="sr-only">Delete modifier</span>
        </Button>
      </div>
    </div>
  );
}

type SortableModifierRowProps = {
  modifier: MenuItemModifier;
  disabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

function SortableModifierRow({ modifier, disabled, onEdit, onDelete }: SortableModifierRowProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: modifier.id,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ModifierRow
        modifier={modifier}
        variant="manager"
        disableActions={disabled}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandleProps={{ attributes, listeners }}
      />
    </div>
  );
}

function formatPrice(cents: number | null | undefined): string {
  if (typeof cents !== 'number') return '$0.00';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function cloneGroupWithModifiers(group: MenuModifierGroup): MenuModifierGroup {
  return {
    ...group,
    modifiers: group.modifiers.map((modifier) => ({ ...modifier })),
  };
}

type GroupFormValues = {
  code: string | null;
  name: string;
  description: string | null;
  selectionMode: MenuModifierGroup['selectionMode'];
  stackingBehavior: MenuModifierGroup['stackingBehavior'];
  minSelections: number;
  maxSelections: number | null;
  allowQuantity: boolean;
  allowCustomPrice: boolean;
  isRequired: boolean;
  sortOrder: number;
};

type GroupSubmission = GroupFormValues & {
  translations: Record<string, { name?: string; description?: string | null }>;
};

type ModifierGroupDialogProps = {
  state: GroupDialogState;
  onClose: () => void;
  onSubmit: (values: GroupSubmission) => Promise<void>;
  isBusy: boolean;
};

function ModifierGroupDialog({ state, onClose, onSubmit, isBusy }: ModifierGroupDialogProps): JSX.Element | null {
  const open = state !== null;
  const initial = state?.initial;

  const [values, setValues] = useState<GroupFormValues>(() => ({
    code: initial?.code ?? null,
    name: initial?.name ?? '',
    description: initial?.description ?? null,
    selectionMode: initial?.selectionMode ?? 'single',
    stackingBehavior: initial?.stackingBehavior ?? 'exclusive',
    minSelections: initial?.minSelections ?? 0,
    maxSelections: initial?.maxSelections ?? null,
    allowQuantity: initial?.allowQuantity ?? false,
    allowCustomPrice: initial?.allowCustomPrice ?? false,
    isRequired: initial?.isRequired ?? false,
    sortOrder: initial?.sortOrder ?? 0,
  }));
  const [spanishName, setSpanishName] = useState(initial?.translations?.es?.name ?? '');
  const [spanishDescription, setSpanishDescription] = useState(
    typeof initial?.translations?.es?.description === 'string'
      ? String(initial?.translations?.es?.description)
      : ''
  );

  useEffect(() => {
    if (!initial) return;
    setValues({
      code: initial.code ?? null,
      name: initial.name,
      description: initial.description ?? null,
      selectionMode: initial.selectionMode,
      stackingBehavior: initial.stackingBehavior,
      minSelections: initial.minSelections ?? 0,
      maxSelections:
        initial.maxSelections === null || initial.maxSelections === undefined
          ? null
          : initial.maxSelections,
      allowQuantity: initial.allowQuantity ?? false,
      allowCustomPrice: initial.allowCustomPrice ?? false,
      isRequired: initial.isRequired ?? false,
      sortOrder: initial.sortOrder ?? 0,
    });
    setSpanishName(initial.translations?.es?.name ?? '');
    setSpanishDescription(
      typeof initial.translations?.es?.description === 'string'
        ? String(initial.translations.es.description)
        : ''
    );
  }, [initial]);

  const handleSubmit = useCallback(async () => {
    if (!values.name.trim()) {
      toast({
        title: 'Name is required',
        variant: 'destructive',
      });
      return;
    }
    const trimmedSpanishName = spanishName.trim();
    if (!trimmedSpanishName) {
      toast({
        title: 'Spanish name is required',
        variant: 'destructive',
      });
      return;
    }
    const trimmedSpanishDescription = spanishDescription.trim();
    if (
      values.maxSelections !== null &&
      values.maxSelections !== undefined &&
      values.maxSelections < values.minSelections
    ) {
      toast({
        title: 'Max selections must be greater than or equal to min selections',
        variant: 'destructive',
      });
      return;
    }
    await onSubmit({
      ...values,
      code: values.code?.trim() || null,
      name: values.name.trim(),
      description: values.description?.trim() || null,
      translations: {
        es: {
          name: trimmedSpanishName,
          description: trimmedSpanishDescription.length > 0 ? trimmedSpanishDescription : null,
        },
      },
    });
  }, [onSubmit, spanishDescription, spanishName, values]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !isBusy && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{state?.mode === 'edit' ? 'Edit modifier group' : 'Add modifier group'}</DialogTitle>
          <DialogDescription>
            Control how guests can select add-ons, variants, and allergen-friendly options.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4 py-1">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="group-name">Name</Label>
                <Input
                  id="group-name"
                  value={values.name}
                  onChange={(event) => setValues((prev) => ({ ...prev, name: event.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="group-name-es">Nombre (Español)</Label>
                <Input
                  id="group-name-es"
                  value={spanishName}
                  onChange={(event) => setSpanishName(event.target.value)}
                  placeholder="Nombre en español"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="group-code">Code</Label>
                <Input
                  id="group-code"
                  value={values.code ?? ''}
                  onChange={(event) =>
                    setValues((prev) => ({ ...prev, code: event.target.value || null }))
                  }
                  placeholder="e.g. SIZE"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="group-description">Description</Label>
              <Textarea
                id="group-description"
                value={values.description ?? ''}
                onChange={(event) =>
                  setValues((prev) => ({
                    ...prev,
                    description: event.target.value || null,
                  }))
                }
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-description-es">Descripción (Español)</Label>
              <Textarea
                id="group-description-es"
                value={spanishDescription}
                onChange={(event) => setSpanishDescription(event.target.value)}
                placeholder="Describe el grupo en español"
                rows={3}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Selection mode</Label>
                <Select
                  value={values.selectionMode}
                  onValueChange={(value: MenuModifierGroup['selectionMode']) =>
                    setValues((prev) => ({ ...prev, selectionMode: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {selectionModes.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <StackingRuleSelector
                value={values.stackingBehavior}
                onChange={(next) => setValues((prev) => ({ ...prev, stackingBehavior: next }))}
                disabled={isBusy}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="group-min-selections">Min selections</Label>
                <Input
                  id="group-min-selections"
                  type="number"
                  min={0}
                  value={values.minSelections}
                  onChange={(event) =>
                    setValues((prev) => ({
                      ...prev,
                      minSelections: Number.parseInt(event.target.value, 10) || 0,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="group-max-selections">Max selections</Label>
                <Input
                  id="group-max-selections"
                  type="number"
                  min={0}
                  value={values.maxSelections ?? ''}
                  onChange={(event) =>
                    setValues((prev) => ({
                      ...prev,
                      maxSelections:
                        event.target.value.trim() === ''
                          ? null
                          : Number.parseInt(event.target.value, 10) || null,
                    }))
                  }
                />
              </div>
            </div>

            <Separator />

            <div className="grid gap-4 sm:grid-cols-2">
              <ToggleField
                label="Required"
                description="Guests must select at least one option."
                checked={values.isRequired}
                onCheckedChange={(checked) =>
                  setValues((prev) => ({ ...prev, isRequired: checked }))
                }
              />
              <ToggleField
                label="Allow quantity"
                description="Let guests add more than one of the same modifier."
                checked={values.allowQuantity}
                onCheckedChange={(checked) =>
                  setValues((prev) => ({ ...prev, allowQuantity: checked }))
                }
              />
              <ToggleField
                label="Custom price"
                description="Allow staff to override modifier pricing."
                checked={values.allowCustomPrice}
                onCheckedChange={(checked) =>
                  setValues((prev) => ({ ...prev, allowCustomPrice: checked }))
                }
              />
              <div className="space-y-2">
                <Label htmlFor="group-sort-order">Sort order</Label>
                <Input
                  id="group-sort-order"
                  type="number"
                  value={values.sortOrder}
                  onChange={(event) =>
                    setValues((prev) => ({
                      ...prev,
                      sortOrder: Number.parseInt(event.target.value, 10) || 0,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isBusy}>
            {isBusy ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type ToggleFieldProps = {
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

function ToggleField({ label, description, checked, onCheckedChange }: ToggleFieldProps): JSX.Element {
  return (
    <div className="flex items-start justify-between rounded-lg border p-3">
      <div>
        <p className="text-sm font-medium">{label}</p>
        {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

type ModifierDialogProps = {
  state: ModifierDialogState;
  onClose: () => void;
  onSubmit: (values: {
    name: string;
    code: string | null;
    sku: string | null;
    externalReference: string | null;
    minQuantity: number;
    maxQuantity: number | null;
    priceDeltaCents: number;
    priceOverrideCents: number | null;
    isDefault: boolean;
    isActive: boolean;
    sortOrder: number;
    allergenTags: string[];
  }) => Promise<void>;
  isBusy: boolean;
};

function ModifierDialog({ state, onClose, onSubmit, isBusy }: ModifierDialogProps): JSX.Element | null {
  const open = state !== null;
  const modifier = state && state.mode === 'edit' ? state.modifier : null;

  const [name, setName] = useState(modifier?.name ?? '');
  const [spanishName, setSpanishName] = useState(modifier?.translations?.es?.name ?? '');
  const [code, setCode] = useState<string>(modifier?.code ?? '');
  const [sku, setSku] = useState<string>(modifier?.sku ?? '');
  const [externalReference, setExternalReference] = useState<string>(modifier?.externalReference ?? '');
  const [minQuantity, setMinQuantity] = useState<number>(modifier?.minQuantity ?? 0);
  const [maxQuantity, setMaxQuantity] = useState<number | null>(
    modifier?.maxQuantity === null || modifier?.maxQuantity === undefined ? null : modifier.maxQuantity,
  );
  const [priceDelta, setPriceDelta] = useState<string>(
    (modifier?.priceDeltaCents ?? 0) / 100 !== 0 ? ((modifier?.priceDeltaCents ?? 0) / 100).toFixed(2) : '0.00',
  );
  const [priceOverride, setPriceOverride] = useState<string>(
    modifier?.priceOverrideCents !== null && modifier?.priceOverrideCents !== undefined
      ? (modifier.priceOverrideCents / 100).toFixed(2)
      : '',
  );
  const [isDefault, setIsDefault] = useState<boolean>(modifier?.isDefault ?? false);
  const [isActive, setIsActive] = useState<boolean>(modifier?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState<number>(modifier?.sortOrder ?? 0);
  const [allergenTags, setAllergenTags] = useState<string[]>(modifier?.allergenTags ?? []);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (!modifier) {
      setName('');
      setCode('');
      setSku('');
      setExternalReference('');
      setMinQuantity(0);
      setMaxQuantity(null);
      setSpanishName('');
      setPriceDelta('0.00');
      setPriceOverride('');
      setIsDefault(false);
      setIsActive(true);
      setSortOrder(0);
      setAllergenTags([]);
      setNewTag('');
      return;
    }

    setName(modifier.name);
    setSpanishName(modifier.translations?.es?.name ?? '');
    setCode(modifier.code ?? '');
    setSku(modifier.sku ?? '');
    setExternalReference(modifier.externalReference ?? '');
    setMinQuantity(modifier.minQuantity ?? 0);
    setMaxQuantity(
      modifier.maxQuantity === null || modifier.maxQuantity === undefined ? null : modifier.maxQuantity,
    );
    setPriceDelta(((modifier.priceDeltaCents ?? 0) / 100).toFixed(2));
    setPriceOverride(
      modifier.priceOverrideCents !== null && modifier.priceOverrideCents !== undefined
        ? (modifier.priceOverrideCents / 100).toFixed(2)
        : '',
    );
    setIsDefault(modifier.isDefault ?? false);
    setIsActive(modifier.isActive ?? true);
    setSortOrder(modifier.sortOrder ?? 0);
    setAllergenTags(modifier.allergenTags ?? []);
    setNewTag('');
  }, [modifier]);

  const handleAddTag = useCallback(() => {
    const tag = newTag.trim();
    if (!tag) return;
    setAllergenTags((prev) => Array.from(new Set([...prev, tag])));
    setNewTag('');
  }, [newTag]);

  const handleRemoveTag = useCallback((tag: string) => {
    setAllergenTags((prev) => prev.filter((value) => value !== tag));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      return;
    }

    const trimmedSpanishName = spanishName.trim();
    if (!trimmedSpanishName) {
      return;
    }

    const parsedDelta = Number.parseFloat(priceDelta || '0');
    if (Number.isNaN(parsedDelta)) {
      return;
    }

    const parsedOverride = priceOverride.trim().length
      ? Number.parseFloat(priceOverride)
      : null;
    if (priceOverride.trim().length && (parsedOverride === null || Number.isNaN(parsedOverride))) {
      return;
    }

    if (maxQuantity !== null && maxQuantity !== undefined && maxQuantity < minQuantity) {
      return;
    }

    await onSubmit({
      name: name.trim(),
      code: code.trim() || null,
      sku: sku.trim() || null,
      externalReference: externalReference.trim() || null,
      minQuantity,
      maxQuantity: maxQuantity === null ? null : maxQuantity,
      priceDeltaCents: Math.round(parsedDelta * 100),
      priceOverrideCents:
        parsedOverride === null ? null : Math.round(Number.isNaN(parsedOverride) ? 0 : parsedOverride * 100),
      isDefault,
      isActive,
      sortOrder,
      allergenTags,
      translations: {
        es: {
          name: trimmedSpanishName,
        },
      },
    });
  }, [
    allergenTags,
    code,
    externalReference,
    isActive,
    isDefault,
    maxQuantity,
    minQuantity,
    name,
    spanishName,
    onSubmit,
    priceDelta,
    priceOverride,
    sku,
    sortOrder,
  ]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !isBusy && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{state?.mode === 'edit' ? 'Edit modifier' : 'Add modifier'}</DialogTitle>
          <DialogDescription>Control pricing, defaults, and allergen tags.</DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label htmlFor="modifier-name">Name</Label>
              <Input
                id="modifier-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="modifier-name-es">Nombre (Español)</Label>
              <Input
                id="modifier-name-es"
                value={spanishName}
                onChange={(event) => setSpanishName(event.target.value)}
                placeholder="Nombre del modificador en español"
                required
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="modifier-code">Code</Label>
                <Input
                  id="modifier-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  placeholder="Optional code"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="modifier-sku">SKU</Label>
                <Input
                  id="modifier-sku"
                  value={sku}
                  onChange={(event) => setSku(event.target.value)}
                  placeholder="Optional SKU"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="modifier-external">External reference</Label>
              <Input
                id="modifier-external"
                value={externalReference}
                onChange={(event) => setExternalReference(event.target.value)}
                placeholder="POS or ERP reference"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="modifier-price-delta">Price delta ($)</Label>
                <Input
                  id="modifier-price-delta"
                  type="number"
                  step="0.01"
                  value={priceDelta}
                  onChange={(event) => setPriceDelta(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="modifier-price-override">Price override ($)</Label>
                <Input
                  id="modifier-price-override"
                  type="number"
                  step="0.01"
                  value={priceOverride}
                  onChange={(event) => setPriceOverride(event.target.value)}
                  placeholder="Leave empty to inherit"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="modifier-min-qty">Min quantity</Label>
                <Input
                  id="modifier-min-qty"
                  type="number"
                  min={0}
                  value={minQuantity}
                  onChange={(event) =>
                    setMinQuantity(Number.parseInt(event.target.value, 10) || 0)
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="modifier-max-qty">Max quantity</Label>
                <Input
                  id="modifier-max-qty"
                  type="number"
                  min={0}
                  value={maxQuantity ?? ''}
                  onChange={(event) =>
                    setMaxQuantity(
                      event.target.value.trim() === ''
                        ? null
                        : Number.parseInt(event.target.value, 10) || null,
                    )
                  }
                  placeholder="Unlimited"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <ToggleField
                label="Default selection"
                checked={isDefault}
                onCheckedChange={setIsDefault}
              />
              <ToggleField label="Active" checked={isActive} onCheckedChange={setIsActive} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="modifier-sort-order">Sort order</Label>
              <Input
                id="modifier-sort-order"
                type="number"
                value={sortOrder}
                onChange={(event) =>
                  setSortOrder(Number.parseInt(event.target.value, 10) || 0)
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Allergen tags</Label>
              <div className="flex flex-wrap gap-2">
                {allergenTags.map((tag) => (
                  <Badge key={tag} variant="outline" className="flex items-center gap-1">
                    {tag}
                    <button
                      type="button"
                      className="rounded-full bg-muted px-1 text-xs"
                      onClick={() => handleRemoveTag(tag)}
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(event) => setNewTag(event.target.value)}
                  placeholder="Add allergen tag"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <Button type="button" variant="secondary" onClick={handleAddTag}>
                  Add
                </Button>
              </div>
            </div>
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isBusy || !name.trim()}>
            {isBusy ? 'Saving…' : 'Save modifier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
